import { useCallback, useEffect, useState, useRef } from "react";
import {
  Select,
  Button,
  Drawer,
  Modal,
  Form,
  Input,
  DatePicker,
  Upload,
  UploadFile,
  UploadProps,
  message,
} from "antd";
import dayjs from "dayjs";
import {
  PlusOutlined,
  PlayCircleFilled,
  UploadOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { DragEndEvent } from "@dnd-kit/core";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext, verticalListSortingStrategy, } from "@dnd-kit/sortable";
import "./VideoTab.scss";
import { VideoDataType, VideoTabProps, TagDataType } from "./types";
import { sortColumn } from "../constants";
import DraggableTableRow from "../components/DraggableTableRow";
import Spinner from "../../../components/layout/spinner/Spinner";
import DrawerFooter from "../../../components/layout/drawer/DrawerFooter";
import MarketDropdown from "../../../components/ui/MarketDropdown";
import TableCustom from "../../../components/ui/TableCustom";
import requiredRule from "../../../utils/formRules/requiredRule";
import videoValidation from "../../../utils/formValidation/videoValidation";
import publishDateRule from "../../../utils/formRules/publishDateRule";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";

import { useCreateVideoMutation } from "../../../graphql/operations/trialMode/testimonial/create-video";
import { useUpdateVideoMutation } from "../../../graphql/operations/trialMode/testimonial/update-video";
import { useDeleteVideoMutation } from "../../../graphql/operations/trialMode/testimonial/delete-video";
import { useUpdateTestimonialSortOrderMutation } from "../../../graphql/operations/trialMode/testimonial/update-video-sort-id";
import { useUploadFileMutation } from "../../../graphql/operations/upload-file";
import { AfBlobPath } from "../../../graphql/operations/@types";

const VideoTab = ({
  tags,
  videos,
  setVideos,
  market,
  setMarket,
  currentUser,
  refetch,
  setLoading,
  category,
}: VideoTabProps) => {
  const [total, setTotal] = useState(0);
  const [displayData, setDisplayData] = useState<VideoDataType[]>([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<VideoDataType | null>();
  const [isDragged, setIsDragged] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [toBeDeletedId, setToBeDeletedId] = useState<number | null>(null);

  const [uploadFile] = useUploadFileMutation();
  const [uploadVideoFile, setUploadVideoFile] = useState<UploadFile[]>([]);
  const [uploadVideoThumbnailFile, setUploadVideoThumbnailFile] = useState<UploadFile[]>([]);

  const [createVideo] = useCreateVideoMutation();
  const [updateVideo] = useUpdateVideoMutation();
  const [deleteVideo] = useDeleteVideoMutation();
  const [updateSortId, { loading: loadingOfUpdateSortId }] = useUpdateTestimonialSortOrderMutation();

  const [tagFilters, setTagFilters] = useState([]);
  const [isWatchOpen, setIsWatchOpen] = useState(false);
  const [previewVideoInfo, setPreviewVideoInfo] =
    useState<VideoDataType | null>();
  const videoRef = useRef<any>();

  const [form] = Form.useForm();

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setIsDragged(true);
      setVideos((previous: VideoDataType[]) => {
        const activeIndex = previous.findIndex((i) => i.testimonialId === active.id);
        const overIndex = previous.findIndex((i) => i.testimonialId === over?.id);
        return arrayMove(previous, activeIndex, overIndex);
      });
    }
  };

  const handleWatchClick = (item: VideoDataType) => {
    setIsWatchOpen(true);
    setPreviewVideoInfo(item);
  };
  const onCancel = () => {
    setIsWatchOpen(false);
    setPreviewVideoInfo(null);
  };

  const handleSubmit = async () => {
    if (uploadVideoFile.length > 0) {
      uploadFile({
        variables: {
          blobs: [
            {
              file: uploadVideoFile[0].originFileObj,
              path: AfBlobPath.TrialModeOnlineExperienceTestimonialVideos,
            },
          ],
        },
      }).catch((e) => console.log(e));
    }

    if (uploadVideoThumbnailFile.length > 0) {
      uploadFile({
        variables: {
          blobs: [
            {
              file: uploadVideoThumbnailFile[0].originFileObj,
              path: AfBlobPath.TrialModeOnlineExperienceTestimonialVideosThumbnails,
            },
          ],
        },
      }).catch((e) => console.log(e));
    }

    const { title, tag, videoFileName, videoThumbnailFileName, publishDate, stopDate } = form.getFieldsValue();
    const payload = {
      testimonialId: selectedVideo?.testimonialId ?? -1,
      market: market,
      title: title,
      tags: tag.map(
        (v1: string) => tags.find((v2: any) => v2.tagName === v1).tagId
      ),
      videoFileName: uploadVideoFile.length > 0 ? uploadVideoFile[0].name : videoFileName[0].name,
      videoThumbnailFileName: uploadVideoThumbnailFile.length > 0 ? uploadVideoThumbnailFile[0].name : videoThumbnailFileName[0].name,
      createdBy: currentUser.userName,
      updatedBy: currentUser.userName,
      pubshingDate: publishDate ? dayjs(publishDate).format("YYYY/MM/DD") : null,
      stopDate: stopDate ? dayjs(stopDate).format("YYYY/MM/DD") : null,
    };
    const callback = {
      success: () => {
        setDrawerOpen(false);
        setSelectedVideo(null);
        setIsEditMode(false);
      },
      finally: () => {
        setLoading(false);
      }
    }

    setLoading(true);
    handleMutation(isEditMode ? updateVideo : createVideo, payload, callback);
  };

  const handleDelete = async () => {
    if (toBeDeletedId) {
      const payload = {
        testimonialId: toBeDeletedId,
      };
      const callback = {
        success: () => {
          setIsDeleteModalOpen(false);
          setToBeDeletedId(null);
        },
        finally: () => {
          setLoading(false);
        }
      }

      handleMutation(deleteVideo, payload, callback);
    }
  };

  const handleMutation = useCallback(
    (
      mutationFn: (arg: any) => Promise<any>,
      variables: any,
      callback?: {
        success?: () => void;
        error?: (e: any) => void;
        finally?: () => void;
      }
    ) => {
      mutationFn({ variables })
        .then(refetch)
        .then(() => {
          callback?.success?.();
        })
        .catch((e: any) => {
          console.error(e);
          callback?.error?.(e);
        })
        .finally(() => {
          callback?.finally?.();
        });
    },
    [refetch]
  );

  const columns: ColumnsType<VideoDataType> = [
    sortColumn,
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 300,
    },
    {
      title: "Tag",
      dataIndex: "tags",
      key: "tags",
      width: 150,
      filters: tagFilters,
      onFilter: (value, record) => record.tags.includes(value as string),
    },
    {
      title: "Video",
      dataIndex: "videoFileUrl",
      key: "videoFileUrl",
      width: 220,
      render: (_, item) => {
        return (
          <div className="video-box">
            <PlayCircleFilled
              className="play-icon"
              onClick={() => handleWatchClick(item)}
            />
            <p>Watch</p>
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "isInUse",
      key: "isInUse",
      width: 115,
      render: (bool, item) => {
        return bool ? (
          <div className="in-use-container">
            <div className="in-use dot" />
            <p>In use</p>
          </div>
        ) : (
          <div className="in-use-container">
            <div className="not-in-use dot" />
            <p>Not in use</p>
          </div>
        );
      },
    },
    {
      title: "Actions",
      width: 150,
      render: (_, item) => {
        return (
          <>
            <Button
              type="link"
              onClick={() => {
                setIsEditMode(true);
                setDrawerOpen(true);
                setSelectedVideo(item);
              }}
              className="edit-button"
            >
              Edit
            </Button>
            {(!item.isInUse) && (
              <Button
                type="link"
                onClick={() => {
                  setIsDeleteModalOpen(true);
                  setToBeDeletedId(item.testimonialId ?? null);
                }}
                className="delete-button"
              >
                Delete
              </Button>
            )}
          </>
        );
      },
    },
  ];

  const videoProps: UploadProps = {
    action: "https://httpbin.org/post",
    maxCount: 1,
    name: "file",
    headers: {
      authorization: "authorization-text",
    },
    beforeUpload: videoValidation(50, "MB"),
    onChange: (info) => {
      console.log(info);
      if (info.fileList.length === 0) {
        setUploadVideoFile([]);
        return;
      }
      setUploadVideoFile(info.fileList);
    },
    onRemove: () => {
      setUploadVideoFile([]);;
    }
  };

  const videoThumbnailProps: UploadProps = {
    action: "https://httpbin.org/post",
    maxCount: 1,
    name: "file",
    headers: {
      authorization: "authorization-text",
    },
    beforeUpload: (file) => {
      const isImage =
        file.type === "image/jpeg" ||
        file.type === "image/jpg" ||
        file.type === "image/png";
      if (!isImage) {
        message.error(renderAntDMessageConfig(`${file.name} is not a JPEG/JPG/PNG file`));
      }

      const isSizeValid = file.size <= 100 * 1024;
      if (!isSizeValid) {
        message.error(renderAntDMessageConfig("Please upload an image that is less than 100KB."));
      }

      return (isImage && isSizeValid) || Upload.LIST_IGNORE;
    },
    onChange: (info) => {
      console.log(info);
      if (info.fileList.length === 0) {
        setUploadVideoThumbnailFile([]);
        return;
      }
      setUploadVideoThumbnailFile(info.fileList);
    },
    onRemove: () => {
      setUploadVideoThumbnailFile([]);;
    }
  };

  useEffect(() => {
    setDisplayData(videos);
    setTotal(videos.length);

    if (isDragged) {
      const payload = { testimonialIds: videos.map((i: VideoDataType) => i.testimonialId) };
      const callback = {
        finally: () => {
          setIsDragged(false)
        }
      }
      handleMutation(updateSortId, payload, callback);
    }
  }, [videos, updateSortId, isDragged, handleMutation]);

  useEffect(() => {
    setTagFilters(
      tags.map((item: TagDataType) => {
        return { text: item.tagName, value: item.tagName };
      })
    );
  }, [tags]);

  useEffect(() => {
    if (selectedVideo) {
      form.setFieldsValue({
        title: selectedVideo.title,
        tag: selectedVideo.tags
          ? selectedVideo.tags.split(', ').filter((t) => t !== '')
          : [],
        videoFileName: selectedVideo.videoFileName ? [{
          uid: "0",
          name: selectedVideo?.videoFileName,
          status: "done",
          url: selectedVideo?.videoFileUrl,
        },] : [],
        videoFileUrl: selectedVideo?.videoFileUrl,
        videoThumbnailFileName: selectedVideo.videoThumbnailFileName ? [{
          uid: "0",
          name: selectedVideo?.videoThumbnailFileName,
          status: "done",
          url: selectedVideo?.videoThumbnailFileUrl,
        },] : [],
        publishDate: selectedVideo.publishDate
          ? dayjs(selectedVideo.publishDate)
          : null,
        stopDate: selectedVideo.stopDate
          ? dayjs(selectedVideo.stopDate)
          : null,
      });
    } else {
      form.resetFields();
    }
  }, [selectedVideo, form]);

  if (loadingOfUpdateSortId) return <Spinner />;

  return (
    <>
      <div id="TrialModeVideoTabWrapper">
        <div className="table-container">
          <div className="select-total">
            <div className="total-container">Total {total} items</div>
            <div>
              <MarketDropdown
                selectedMarket={market}
                setSelectedMarket={setMarket}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className="create-button"
                onClick={() => {
                  setDrawerOpen(true);
                }}
              >
                Create New
              </Button>
            </div>
          </div>
          <div className="table-container no-pt">
            <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
              <SortableContext
                items={videos.map((i: VideoDataType) => i.testimonialId)}
                strategy={verticalListSortingStrategy}
              >
                <TableCustom
                  dataSource={displayData || []}
                  columns={columns}
                  pagination={false}
                  components={{
                    body: {
                      row: DraggableTableRow,
                    },
                  }}
                  rowKey="testimonialId"
                />
              </SortableContext>
            </DndContext>
          </div>
        </div>
      </div>
      <Drawer
        open={drawerOpen}
        width={500}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedVideo(null);
          setIsEditMode(false);
        }}
        title={isEditMode ? "Edit video" : "Create new video"}
        footer={
          <DrawerFooter
            handleSubmit={() => {
              form
                .validateFields()
                .then(() => {
                  handleSubmit();
                })
                .catch((errorInfo) => {
                  console.error("Validation Failed:", errorInfo);
                });
            }}
            handleCancel={() => {
              setDrawerOpen(false);
              setSelectedVideo(null);
              setIsEditMode(false);
            }}
          />
        }
      >
        <div id="videoDrawerWrapper">
          <div>
            <p className="title">Market</p>
            <p className="content">{market}</p>
          </div>
          <Form form={form} layout="vertical">
            <Form.Item
              className="form-item"
              label="Title"
              rules={[requiredRule]}
              name="title"
            >
              <Input />
            </Form.Item>
            <Form.Item
              className="form-item"
              label="Tag"
              name="tag"
            >
              <Select
                placeholder="- Select -"
                options={tags.map((item: any) => {
                  return { label: item.tagName, value: item.tagName };
                })}
                mode="multiple"
              />
            </Form.Item>
            <Form.Item
              className="form-item"
              label="Video file"
              rules={[requiredRule]}
              name="videoFileName"
              valuePropName="fileList"
              getValueFromEvent={(e) => e.fileList}
              extra={"The uploaded video will be stored on the blob server. Maximum file size: 50MB."}
            >
              <Upload
                {...videoProps}
              >
                <Button icon={<UploadOutlined />} >
                  Click to Upload
                </Button>
              </Upload>
            </Form.Item>
            <Form.Item
              className="form-item"
              label="Video thumbnail"
              rules={[requiredRule]}
              name="videoThumbnailFileName"
              valuePropName="fileList"
              getValueFromEvent={(e) => e.fileList}
              extra={"Recommended resolution is 350px * 600px with file size less than 100KB. Formats: JPEG, JPG, PNG"}
            >
              <Upload
                {...videoThumbnailProps}
              >
                <Button icon={<UploadOutlined />} >
                  Click to Upload
                </Button>
              </Upload>
            </Form.Item>
            <Form.Item
              name="publishDate"
              label="Publish date"
              rules={[publishDateRule]}
              dependencies={["stopDate"]}
            >
              <DatePicker
                style={{ width: "100%" }}
                placeholder="Select date"
              />
            </Form.Item>
            <Form.Item
              name="stopDate"
              label="Stop date"
              extra={"If no stop date is selected, this page will appear permanently"}
            >
              <DatePicker
                style={{ width: "100%" }}
                placeholder="Select date"
                disabledDate={(current: any) =>
                  current && current < dayjs().endOf("day")
                }
              />
            </Form.Item>
          </Form>
        </div>
      </Drawer>
      <Modal
        open={isWatchOpen}
        onCancel={onCancel}
        title={previewVideoInfo?.title}
        footer={null}
        width={410}
        forceRender
      >
        {previewVideoInfo && (
          <iframe
            ref={videoRef}
            src={previewVideoInfo.videoFileUrl}
            width="360"
            height="300"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title={previewVideoInfo.title}
          >
            Your browser does not support the video tag.
          </iframe>
        )}
      </Modal>
      <Modal
        centered
        open={isDeleteModalOpen}
        closeIcon={null}
        footer={[
          <div className="modal-button-group">
            <Button
              onClick={() => {
                setIsDeleteModalOpen(false);
                setToBeDeletedId(null);
              }}
              style={{ marginRight: 8, marginLeft: 8, marginBottom: 4 }}
            >
              No
            </Button>
            <Button
              danger
              onClick={handleDelete}
              style={{ marginRight: 8, marginLeft: 8, marginBottom: 4 }}
            >
              Delete
            </Button>
          </div>,
        ]}
        width={417}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            height: 76,
            marginBottom: 24,
            marginRight: 8,
            marginLeft: 8,
            marginTop: 12,
          }}
        >
          <img
            alt="warning circle icon"
            src={require("../../../assets/icons/warning-circle.png")}
            style={{ width: 22, height: 22, marginRight: 16 }}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <p style={{ fontSize: 16, fontWeight: 500 }}>
              Are you sure you want to delete the item?
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default VideoTab;
